import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { Filter, FilterService } from 'src/app/shared/services/filter/filter.service'
import { firstValueFrom, map, Subscription, switchMap, take } from 'rxjs'
import { FieldColumnsComponent } from '../../field-columns/field-columns.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { EntitySelectorComponent } from '../../entity-selector/entity-selector.component'
import { EntityRef } from 'src/app/shared/models/referenceData'
import { EntityFilter } from 'src/app/shared/services/filter/entity-filter'
import { ReferenceDataSource } from '../../reference-data/base-reference.service'
import { BoolDisplayPipe } from 'src/app/shared/pipes/boolDisplay.pipe'
import { FlagFilter } from 'src/app/shared/services/filter/flag-filter'
import { TextInputComponent } from '../../text-input/text-input.component'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import { ServicePointManagementService } from '../../service-point-management/service-point-management.service'
import { DateRangeFilter, DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { formatAsUTC, utc } from 'src/app/shared/utilities/date'
import { ToastService } from '../../toasts/toasts.service'
import { NON_TERMINATING_NAMED_DATE } from 'src/app/shared/models/namedDate'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    FieldColumnsComponent,
    LabeledPropertyComponent,
    TextInputComponent,
    EntitySelectorComponent,
    DateRangeFilterPickerComponent,
  ],
  selector: 'app-create-service-point-modal',
  styles: '',
  template: `
    <div app-modal-header>Create Service Point</div>
    <div app-modal-body>
      <p>
        Creates a non-terminating service point with a given ID, effective today
      </p>
      <div app-field-columns>
        <div app-labeled-property label="Service Point ID">
          <div
            app-text-input
            placeholder="Required"
            (valueChange)="
              handleText($event, 'service_point_id')
            "
          ></div>
        </div>
        <div app-labeled-property label="Effective Date">
          <div
            dateRangeFilterPicker
            [showRange]="false"
            [showOperation]="false"
            [showTime]="false"
            [showSeconds]="false"
            placeholder="Required"
            (valueChange)="
              handleDate($event, 'effective_date')
            "
          ></div>
        </div>
        <div app-labeled-property label="MV90">
          <div
            app-text-input
            placeholder="Optional"
            (valueChange)="handleText($event, 'mv90_id')"
          ></div>
        </div>
        <div app-labeled-property label="Address">
          <div
            app-text-input
            placeholder="Required"
            (valueChange)="handleText($event, 'location')"
          ></div>
        </div>
      </div>
      <hr />
      <p>
        Provide attributes for the newly created service point. You must provide
        all attributes.
      </p>
      <div app-field-columns>
        @for(entity of entities; track entity.name) {
          @if(state[entity.name]){
            <div app-labeled-property [label]="entityFilter(entity.name).config.meta.fieldDisplay">
              <div
                app-entity-selector
                [slim]="true"
                [selectedEntities]="entityFilter(entity.name).entities"
                (valueChange)="handleEntity($event, entity.name)"
                [label]="entityFilter(entity.name).config.meta.fieldDisplay"
                [config]="entityFilter(entity.name).config"
              ></div>
            </div>
          }
        }
      </div>
      <div app-modal-footer>
        <button
          class="action-btn primary"
          [disabled]="submitDisabled"
          (click)="onSubmit()"
        >
          Submit
        </button>
        <button class="action-btn" (click)="onCancel()">Close</button>
      </div>
    </div>
  `,
})
export class CreateServicePointModalComponent implements OnDestroy {
  activeModal = inject(NgbActiveModal)
  toast = inject(ToastService)
  filterService = inject(FilterService)
  servicePointManagementService = inject(ServicePointManagementService)
  changeDetectorRef = inject(ChangeDetectorRef)

  flagOptions = [
    { value: undefined, display: 'None Selected' },
    { value: true, display: new BoolDisplayPipe().transform(true) },
    { value: false, display: new BoolDisplayPipe().transform(false) },
  ]
  flags = [
    {
      name: 'mdma_subtract_from_adjusted_system_load_flag',
      display: 'MDMA Subtract From Adjusted System Load',
    },
    {
      name: 'mdma_add_to_adjusted_system_load_flag',
      display: 'MDMA Add To Adjusted System Load',
    },
    {
      name: 'mdma_aggregation_exclusion_flag',
      display: 'MDMA Aggregation Exclusion',
    },
    {
      name: 'mdma_weather_sensitive_override_flag',
      display: 'MDMA Weather Sensitive Override',
    },
    {
      name: 'tbill_subtract_from_adjusted_system_load_flag',
      display: 'TBill Subtract From Adjusted System Load',
    },
    {
      name: 'tbill_add_to_adjusted_system_load_flag',
      display: 'TBill Add To Adjusted System Load',
    },
    {
      name: 'tbill_aggregation_exclusion_flag',
      display: 'TBill Aggregation Exclusion',
    },
    {
      name: 'tbill_weather_sensitive_flag',
      display: 'TBill Weather Sensitive',
    },
    {
      name: 'bmg_flag',
      display: 'BMG',
    },
  ]
  entities: {
    name: string
    display: string
    resource: ReferenceDataSource
  }[] = [
    {
      name: 'ldc_code',
      display: 'LDC',
      resource: 'localDistributionCompanies',
    },
    {
      name: 'eldc_code',
      display: 'ELDC',
      resource: 'localDistributionCompanies',
    },
    {
      name: 'supply_class_code',
      display: 'Supply Class',
      resource: 'supplyClasses',
    },
    {
      name: 'profile_class_code',
      display: 'Profile Class',
      resource: 'profileClasses',
    },
    {
      name: 'retailer_code',
      display: 'Retailer',
      resource: 'retailers',
    },
    {
      name: 'loss_class_code',
      display: 'Loss Class',
      resource: 'lossClasses',
    },
    {
      name: 'location_code',
      display: 'Location',
      resource: 'locations',
    },
    {
      name: 'meter_type_code',
      display: 'Meter Type',
      resource: 'meterTypes',
    },
  ]
  submitDisabled = true
  submitDisabled$ = this.filterService.state(CreateServicePointModalComponent.name).pipe(
    map((state) => {
      // required
      const service_point_id = (
        state['service_point_id'] as StringFilter | undefined
      )?.state.values[0]
      const effective_date = (state['effective_date'] as DateRangeFilter | undefined)
        ?.state.value as Date
      const location = (state['location'] as StringFilter | undefined)
        ?.state.values[0]
      const entity_vals = this.entities.map(
        (entity) => (state[entity.name] as EntityFilter | undefined)?.state.entities[0],
      )
      const flag_vals = this.flags.map(
        (flag) => (state[flag.name] as FlagFilter | undefined)?.state.value,
      )

      // conditions
      const required = [service_point_id, effective_date, location]

      return !(
        required.every(Boolean) &&
        entity_vals.every((o) => o !== undefined) &&
        (flag_vals.every((f) => f !== undefined) ||
          flag_vals.every((f) => f === undefined))
      )
    }),
  )

  private subscription = new Subscription()
  private onSuccess: () => void = () => void 0
  public state: Record<string, Filter> = {}

  async init(onSuccess: () => void = () => void 0) {
    this.onSuccess = onSuccess
    this.changeDetectorRef.detectChanges()
    const { dispatch } = this.filterService
    const entityFilters: EntityFilter[] = []
    for (const e of this.entities) {
      entityFilters.push(new EntityFilter({
        entities: [],
        config: {
          resource: e.resource,
          allowMultiple: false,
          meta: {
            fieldGroup: CreateServicePointModalComponent.name,
            fieldName: e.name,
            fieldDisplay: e.display,
          },
        },
      }, dispatch))
    }

    dispatch(FilterService.InitFiltersAction({
      fieldGroup: CreateServicePointModalComponent.name,
      fields: {
        service_point_id: new StringFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: CreateServicePointModalComponent.name,
              fieldName: 'service_point_id',
              fieldDisplay: 'Service Point ID',
            },
          },
        }, dispatch),
        effective_date: new DateRangeFilter({
          config: {
            showRange: false,
            showOperation: false,
            showSeconds: false,
            showTime: false,
            sendTime: true,
            meta: {
              fieldGroup: CreateServicePointModalComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective',
            },
          },
        }, dispatch),
        mv90_id: new StringFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: CreateServicePointModalComponent.name,
              fieldName: 'mv90_id',
              fieldDisplay: 'MV90',
            },
          },
        }, dispatch),
        location: new StringFilter({
          config: {
            allowMultiple: false,
            meta: {
              fieldGroup: CreateServicePointModalComponent.name,
              fieldName: 'location',
              fieldDisplay: 'Address',
            },
          },
        }, dispatch),
        ...entityFilters.reduce((acc, e) => ({ ...acc, [e.state.config.meta.fieldName]: e }), {}),
      },
    }))
    this.subscription.add(
      this.filterService.state(CreateServicePointModalComponent.name).subscribe(
        state => this.state = state,
      ),
    )
    this.subscription.add(
      this.submitDisabled$.subscribe(submitDisabled => this.submitDisabled = submitDisabled),
    )
  }

  entityFilter(key: string) {
    return (this.state[key] as EntityFilter)?.state
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  onCancel() {
    this.activeModal.dismiss()
  }

  async onSubmit() {
    await firstValueFrom(
      this.filterService.state(CreateServicePointModalComponent.name).pipe(
        take(1),
        switchMap((state) => {
          return this.servicePointManagementService.createServicePoint({
            service_point_id:
              (state['service_point_id'] as StringFilter)?.state.values[0] ?? '',
            effective_date: formatAsUTC(
              ((state['effective_date'] as DateRangeFilter)?.state.value as Date) ?? utc(),
            ),
            termination_date: formatAsUTC(
              ((state['termination_date'] as DateRangeFilter)?.state.value as Date) ?? NON_TERMINATING_NAMED_DATE.value,
            ),
            location: (state['location'] as StringFilter)?.state.values[0] ?? '',
            mv90_id: (state['mv90_id'] as StringFilter)?.state.values[0] ?? '',

            ldc_code: (state['ldc_code'] as EntityFilter)?.state.entities[0].entity_code,
            eldc_code: (state['eldc_code'] as EntityFilter)?.state.entities[0]
              .entity_code,
            profile_class_code: (state['profile_class_code'] as EntityFilter)
              ?.state.entities[0].entity_code,
            retailer_code: (state['retailer_code'] as EntityFilter)?.state.entities[0]
              .entity_code,
            supply_class_code: (state['supply_class_code'] as EntityFilter)
              ?.state.entities[0].entity_code,
            meter_type_code: (state['meter_type_code'] as EntityFilter)
              ?.state.entities[0].entity_code,
            loss_class_code: (state['loss_class_code'] as EntityFilter)
              ?.state.entities[0].entity_code,
            location_code: (state['location_code'] as EntityFilter)?.state.entities[0]
              .entity_code,
          })
        }),
      ),
    )
      .then(() => {
        this.onSuccess()
        this.activeModal.dismiss()
      })
      .catch(() =>{
        this.toast.show({ message: 'An Error Occurred', type: 'error' })
      })
  }

  handleDate(val: DateRangeFilterStateValue, field: string) {
    this.filterService.dispatch(
      DateRangeFilter.SetDateRangeFilterValueAction(val, {
        fieldGroup: CreateServicePointModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  handleText(val: string, field: string) {
    this.filterService.dispatch(
      StringFilter.SetStringsAction({
        values: [val],
      }, {
        fieldGroup: CreateServicePointModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  handleEntity(
    entityRef: EntityRef,
    field: string,
  ) {
    this.filterService.dispatch(
      EntityFilter.AddEntityRefsAction({
        entities: [entityRef],
      }, {
        fieldGroup: CreateServicePointModalComponent.name,
        fieldName: field,
      }),
    )
  }

  handleBool(
    value: (typeof this.flagOptions)[number],
    field: string,
  ) {
    this.filterService.dispatch(
      FlagFilter.SetFlagFilterValueAction({
        value: value.value ?? null,
      },
      {
        fieldGroup: CreateServicePointModalComponent.name,
        fieldName: field,
      }),
    )
  }
}
